import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2Icon, PencilIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import useDialog from '@/hooks/useDialog';
import server from '@/services/server';
import { useClub } from '../ClubContext';
import useClubErrors from '../hooks/useClubErrors';
import useClubPortalContainer from '../hooks/useClubPortalContainer';
import ClubPhoneInput from './ClubPhoneInput';
import ClubSettingsCardHeader from './ClubSettingsCardHeader';

const clubDetailsSchema = z.object({
    name: z.string().min(1, 'Nome é obrigatório'),
    supportEmail: z.string().email('E-mail inválido').optional().or(z.literal('')),
    supportPhone: z
        .string()
        .min(1, 'Telefone WhatsApp é obrigatório')
        .refine(value => String(value).replace(/\D/g, '').length >= 10, {
            message: 'Informe um telefone WhatsApp válido',
        }),
    supportWebsite: z.string().optional(),
});

function DialogEditClubDetails({ data, mutate, open, onOpenChange }) {
    const { setErrors, enqueueSnackbar } = useClubErrors();
    const portalContainer = useClubPortalContainer();

    const form = useForm({
        resolver: zodResolver(clubDetailsSchema),
        defaultValues: {
            name: '',
            supportEmail: '',
            supportPhone: '',
            supportWebsite: '',
        },
    });

    useEffect(() => {
        if (!open || !data) return;

        form.reset({
            name: data.name || '',
            supportEmail: data.supportEmail || '',
            supportPhone: data.supportPhone || '',
            supportWebsite: data.supportWebsite || '',
        });
    }, [open, data, form]);

    const onSubmit = async values => {
        try {
            await server.put('club', values);
            await mutate();
            enqueueSnackbar('Clube atualizado com sucesso');
            onOpenChange(false);
        } catch (error) {
            setErrors(error);
        }
    };

    const { errors, isSubmitting } = form.formState;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent container={portalContainer} className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>Editar clube</DialogTitle>
                    <DialogDescription>Atualize as informações básicas e de suporte do seu clube.</DialogDescription>
                </DialogHeader>

                <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-6">
                    <FieldGroup>
                        <Field data-invalid={Boolean(errors.name)}>
                            <FieldLabel htmlFor="club-name">
                                Nome do clube <span className="text-destructive">*</span>
                            </FieldLabel>
                            <Input id="club-name" {...form.register('name')} aria-invalid={Boolean(errors.name)} />
                            <FieldError>{errors.name?.message}</FieldError>
                        </Field>

                        <Field data-invalid={Boolean(errors.supportEmail)}>
                            <FieldLabel htmlFor="club-support-email">E-mail de suporte</FieldLabel>
                            <Input
                                id="club-support-email"
                                type="email"
                                {...form.register('supportEmail')}
                                aria-invalid={Boolean(errors.supportEmail)}
                            />
                            <FieldError>{errors.supportEmail?.message}</FieldError>
                        </Field>

                        <Field data-invalid={Boolean(errors.supportPhone)}>
                            <FieldLabel htmlFor="club-support-phone">
                                Telefone WhatsApp <span className="text-destructive">*</span>
                            </FieldLabel>
                            <Controller
                                name="supportPhone"
                                control={form.control}
                                render={({ field }) => (
                                    <ClubPhoneInput
                                        id="club-support-phone"
                                        name={field.name}
                                        value={field.value ?? ''}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        aria-invalid={Boolean(errors.supportPhone)}
                                    />
                                )}
                            />
                            <FieldError>{errors.supportPhone?.message}</FieldError>
                        </Field>

                        <Field>
                            <FieldLabel htmlFor="club-support-website">Site de suporte</FieldLabel>
                            <Input id="club-support-website" {...form.register('supportWebsite')} />
                        </Field>
                    </FieldGroup>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Cancelar
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? <Spinner className="size-4" /> : 'Salvar'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function DetailsClub({ data, mutate }) {
    const { open, handleClose } = useDialog('#my-club');
    const { health } = useClub();
    const attention = health?.checks?.supportPhone?.ok === false;

    return (
        <>
            <Card>
                <CardHeader>
                    <ClubSettingsCardHeader
                        icon={Building2Icon}
                        title="Detalhes do clube"
                        description="Nome e canais de suporte."
                        attention={attention}
                    />
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div>
                        <span className="text-muted-foreground">Nome: </span>
                        <span className="font-medium">{data?.name || '—'}</span>
                    </div>
                    <div>
                        <span className="text-muted-foreground">E-mail: </span>
                        <span className="font-medium">{data?.supportEmail || '—'}</span>
                    </div>
                    <div>
                        <span className="text-muted-foreground">WhatsApp: </span>
                        <span className="font-medium">{data?.supportPhone || '—'}</span>
                    </div>
                </CardContent>
                <CardFooter>
                    <Button variant="outline" size="sm" render={<Link to="#my-club" />}>
                        <PencilIcon className="size-4" />
                        Editar
                    </Button>
                </CardFooter>
            </Card>

            <DialogEditClubDetails
                data={data}
                mutate={mutate}
                open={open}
                onOpenChange={isOpen => !isOpen && handleClose()}
            />
        </>
    );
}
